import { Dropdown, Menu, Input } from '@arco-design/web-react';
import classnames from 'classnames';
import * as React from 'react';
import { Component, CustomComponentManager, isNil } from '@easytwin/runtime';
import { DropListMenuOption } from '@/UIGenerator';
import { useDrag, useDrop } from 'react-dnd';
import { DragDropItemId } from '@/constants/dragDropItemId';
import { HintTooltip } from '../HintTooltip';

interface FunctionalCompTabProps {
  item: Component;
  ref: React.LegacyRef<HTMLDivElement>;
  droplist: DropListMenuOption[];
  selectedStateId: string | undefined;
  renamingStateId: string | undefined;
  onRenameStateStart: (id: string) => void;
  onRenameStateEnd: (value: string) => void;
  onTabChange?: (id: string) => void;
  onSort: (originId: string, toId: string, state: HoverState) => void;
}

export type HoverState = 'left' | 'right' | 'none';

export const FunctionalCompTab = React.forwardRef(
  (props: FunctionalCompTabProps, ref: React.ForwardedRef<HTMLDivElement>) => {
    const {
      item,
      droplist,
      selectedStateId,
      renamingStateId,
      onRenameStateStart,
      onRenameStateEnd,
      onTabChange,
      onSort,
    } = props;
    const [hoverState, setHoverState] = React.useState<HoverState>('none');
    const innerRef = React.useRef<HTMLDivElement | null>(null);

    // 将父组件的 ref 和子组件的 ref 指向同一个 DOM
    React.useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(innerRef.current);
        } else {
          ref.current = innerRef.current;
        }
      }
    }, []);

    const [{ isDragging }, drag, dragPreview] = useDrag(
      () => ({
        type: DragDropItemId.StateTab,
        item: { id: item.id },
        isDragging: (monitor) => {
          return monitor.getItem().id === item.id;
        },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
      }),
      [],
    );

    const [{ isOver, canDrop }, drop] = useDrop(
      () => ({
        accept: DragDropItemId.StateTab,
        drop: (source: { id: string }, monitor) => {
          onSort(source.id, item.id, hoverState);
        },
        canDrop: (source) => {
          return source.id !== item.id;
        },
        hover: (_, monitor) => {
          const clientOffset = monitor.getClientOffset();
          if (!monitor.canDrop()) {
            setHoverState('none');
            return;
          }
          if (!innerRef.current || isNil(clientOffset)) {
            setHoverState('none');
            return;
          }
          let state: HoverState = 'none';
          const rect = innerRef.current.getBoundingClientRect();
          const pointerOffsetLeft = clientOffset.x - rect.x;

          if (pointerOffsetLeft > 0 && pointerOffsetLeft < rect.width) {
            const left = pointerOffsetLeft < rect.width / 2;
            state = left ? 'left' : 'right';
          }

          setHoverState(state);
          // console.log(
          //   'pointerOffsetTop',
          //   clientOffset?.y,
          //   treeNodeRef.current?.getBoundingClientRect().y,
          //   pointerOffsetTop,
          // );
        },

        collect: (monitor) => ({
          isOver: monitor.isOver({ shallow: true }),
          canDrop: monitor.canDrop(),
        }),
      }),
      [selectedStateId, item.id, hoverState],
    );

    React.useEffect(() => {
      if (!isOver) {
        setHoverState('none');
      }
    }, [isOver]);

    return (
      <Dropdown
        droplist={
          <Menu>
            {droplist.map((o) => {
              return o.visible === false ? null : (
                <Menu.Item key={o.key} onClick={o.onSelected} style={{ height: 32, width: 135 }}>
                  {o.icon}
                  <div className='ml-2'>{o.label}</div>
                </Menu.Item>
              );
            })}
          </Menu>
        }
        trigger={'contextMenu'}
      >
        <div
          ref={(node) => {
            innerRef.current = node;
            return dragPreview(drop(drag(node)));
          }}
          className={classnames(
            'px-[6px] cursor-pointer whitespace-pre flex justify-center items-center relative',
            item.id === selectedStateId && !isDragging ? 'text-white' : '',
            `drag-${hoverState}`,
            isDragging ? 'text-text75788133' : 'hover:text-white',
          )}
          onContextMenu={() => {
            onTabChange?.(item.id);
          }}
          onClick={() => onTabChange?.(item.id)}
          onDoubleClick={() => {
            onRenameStateStart(item.id);
          }}
        >
          {renamingStateId === item.id ? (
            <Input
              autoFocus
              maxLength={24}
              className='!w-20 !h-6'
              defaultValue={item.name}
              onPressEnter={(e) => {
                e.target.blur();
              }}
              onBlur={(e: any) => {
                const value = e.target.value;
                onRenameStateEnd(value);
              }}
            />
          ) : (
            <HintTooltip
              content={`${
                item.engine.getManager(CustomComponentManager)?.get(item.type, item.version)
                  ?.Settings.name || item.name
              } - v${item.version}`}
            >
              <div
                className={classnames(
                  'max-w-32',
                  'whitespace-nowrap',
                  'block',
                  'overflow-hidden',
                  'text-ellipsis',
                )}
              >
                {item.name}
              </div>
            </HintTooltip>
          )}
        </div>
      </Dropdown>
    );
  },
);
